import { Box, Collapse, Flex, Heading, Icon } from '@chakra-ui/react';
import {
  ExternalIngestionDocumentFragment,
  ExternalValidationReportDataFragment,
  GetIngestionDocumentFragment,
  IngestionDocumentReviewFragment,
  IngestionDocumentValidationErrorReviewFragment,
} from '@client/graphql/__generated__/types';
import { ChevronDownIcon } from 'lucide-react';
import { FC, ReactNode, useRef, useState } from 'react';
import { ComplianceDocumentSidebarItem } from './ComplianceDocumentSidebarItem';

interface UngroupedDocumentsSectionProps {
  title?: ReactNode;
  documents: ExternalIngestionDocumentFragment[];
  validationData: ExternalValidationReportDataFragment | null;
  selectedDocument: GetIngestionDocumentFragment | null;
  documentReviews?: IngestionDocumentReviewFragment[];
  submittedDocumentReviews?: IngestionDocumentReviewFragment[];
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[];
  shouldShowStatus?: boolean;
  onDocumentSelect: (
    document: ExternalIngestionDocumentFragment,
    contractDocumentId?: string
  ) => void;
  isCollapsible?: boolean;
  defaultCollapsed?: boolean;
  children?: ReactNode;
  showSubtitle?: boolean;
  subtitleType?: 'folder' | 'status';
  highlighted?: boolean;
}

export const UngroupedDocumentsSection: FC<UngroupedDocumentsSectionProps> = ({
  title,
  documents,
  validationData,
  selectedDocument,
  shouldShowStatus = true,
  documentReviews,
  submittedDocumentReviews,
  hiddenDocumentValidationReviews,
  onDocumentSelect,
  isCollapsible = false,
  defaultCollapsed = true,
  children,
  showSubtitle = false,
  subtitleType,
  highlighted = false,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const lastDocumentRef = useRef<HTMLDivElement>(null);

  const toggleCollapse = () => {
    if (isCollapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  const headingContent = (
    <Flex align="center">
      {title && (
        <Heading color="compliance.text.secondary" size="smallUppercase">
          <Flex align="center" gap={1}>
            {title} ({documents.length})
          </Flex>
        </Heading>
      )}

      {isCollapsible && (
        <Icon
          as={ChevronDownIcon}
          color="compliance.text.secondary"
          h={4}
          ml={2}
          transform={isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)'}
          transition="transform 0.2s"
          w={4}
        />
      )}
    </Flex>
  );

  const content = (
    <>
      {isCollapsible ? (
        <Box
          _hover={{ opacity: 0.8 }}
          cursor="pointer"
          pb={1}
          onClick={toggleCollapse}
        >
          {headingContent}
        </Box>
      ) : (
        headingContent
      )}
      <Collapse animateOpacity in={!isCollapsed || !isCollapsible}>
        <Flex direction="column" gap={0}>
          {documents.map((document, index) => {
            const isDocumentSelected = selectedDocument?.id === document.id;

            return (
              <Box
                key={document.id}
                ref={index === documents.length - 1 ? lastDocumentRef : null}
                borderRadius="md"
                py={1}
              >
                <ComplianceDocumentSidebarItem
                  documentItem={{
                    document,
                    contractDocumentId: document.contractDocuments?.[0]?.id,
                  }}
                  documentReviews={documentReviews}
                  hiddenDocumentValidationReviews={
                    hiddenDocumentValidationReviews
                  }
                  isSelected={isDocumentSelected}
                  shouldShowStatus={shouldShowStatus}
                  showSubtitle={showSubtitle}
                  submittedDocumentReviews={submittedDocumentReviews}
                  subtitleType={subtitleType}
                  validationData={validationData}
                  onSelect={onDocumentSelect}
                />
              </Box>
            );
          })}
          {children}
        </Flex>
      </Collapse>
    </>
  );

  return highlighted ? (
    <Box
      bg="compliance.sidebar.highlighted.bg"
      border="1px solid"
      borderColor="compliance.sidebar.highlighted.border"
      borderRadius="md"
      p={3}
    >
      {content}
    </Box>
  ) : (
    content
  );
};
